import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { isBefore, isAfter, isWithinInterval } from 'date-fns';
import {
  Container, Content, Card,
  LoadingContainer,
} from './styles';
import BackArrow from '../../components/BackArrow';

import VirtualRaceHeader from '../../components/VirtualRaceHeader';
import VirtualRaceSubscription from './VirtualRaceSubscription';
import VirtualRaceGoal from './VirtualRaceGoal';
import VirtualRaceProgress from './VirtualRaceProgress';
import coreApi from '../../services/coreApi';
import { RootState } from '../../store';
import Loader from '../../components/Loader';
import { api2 } from '../../services/api';

import { UserVirtualRaceType, VirtualRaceType } from '../../types/VirtualRace';
import { Params } from '../../types/Challenger';
import VirtualRaceWaiting from './VirtualRaceWaiting';
import { Activity, getActivities } from '../../store/Activities.store';
import VirtualRaceActivity from '../../components/VirtualRaceActivity';
import VirtualRaceIntegrationCard from '../../components/VirtualRaceIntegrationCard';
import VirtualRaceOutOfDate from './VirtualRaceOutOfDate';

export enum VirtualRaceStatesTypes {
  SUBSCRIPTION = 'SUBSCRIPTION',
  SETTING_GOAL = 'SETTING_GOAL',
  WAITING_START = 'WAITING_START',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_VALIDATION = 'WAITING_VALIDATION',
  FINISHED = 'FINISHED',
  OUT_OF_DATE = 'OUT_OF_DATE',
}

export default function VirtualRace() {
  const { slug } = useParams<Params>();

  const { user, token } = useSelector((state: RootState) => state.auth);
  const { company_id, companies } = user;

  const allActivitiesFromStore = useSelector((state: RootState) => state.activities.activities);

  const [state, setState] = useState<VirtualRaceStatesTypes>(VirtualRaceStatesTypes.SUBSCRIPTION);
  const [userVirtualRace, setUserVirtualRace] = useState<UserVirtualRaceType | undefined>();
  const [virtualRace, setVirtualRace] = useState<VirtualRaceType>();

  const [activities, setActivities] = useState<Activity[]>([]);

  const [showIntegrationCard, setShowIntegrationCard] = useState(true);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();

  const refreshRegisterActivity = async () => {
    const isValid = await validateActivities();
    if (isValid && userVirtualRace) {
      if (userVirtualRace.status !== VirtualRaceStatesTypes.FINISHED) {
        await updateUserVirtualRace(VirtualRaceStatesTypes.FINISHED);
      }
      setState(VirtualRaceStatesTypes.FINISHED);
    }
  };

  const renderCurrentState = () => {
    const stateMap: Record<VirtualRaceStatesTypes, JSX.Element> = {
      [VirtualRaceStatesTypes.SUBSCRIPTION]: (
        <VirtualRaceSubscription virtualRace={virtualRace!} />
      ),
      [VirtualRaceStatesTypes.SETTING_GOAL]: (
        <VirtualRaceGoal
          userVirtualRace={userVirtualRace!}
          setUserVirtualRace={setUserVirtualRace}
          virtualRace={virtualRace!}
          action={() => setState(VirtualRaceStatesTypes.IN_PROGRESS)}
        />
      ),
      [VirtualRaceStatesTypes.WAITING_START]: (
        <VirtualRaceWaiting
          virtualRace={virtualRace!}
        />
      ),
      [VirtualRaceStatesTypes.IN_PROGRESS]: (
        <VirtualRaceProgress
          virtualRace={virtualRace!}
          userVirtualRace={userVirtualRace!}
          allActivities={allActivitiesFromStore}
          refresh={refreshRegisterActivity}
          setUserVirtualRace={setUserVirtualRace}
        />
      ),
      [VirtualRaceStatesTypes.WAITING_VALIDATION]: (
        <VirtualRaceActivity activities={activities!} userVirtualRace={userVirtualRace!} userId={user.id} />
      ),
      [VirtualRaceStatesTypes.OUT_OF_DATE]: (
        <VirtualRaceOutOfDate
          virtualRace={virtualRace!}
          userVirtualRace={userVirtualRace!}
          allActivities={allActivitiesFromStore}
        />
      ),
      [VirtualRaceStatesTypes.FINISHED]: (
        <VirtualRaceActivity activities={activities!} userVirtualRace={userVirtualRace!} userId={user.id} hasMedalRequest={virtualRace?.hasMedalRequest} isValidated />
      ),
    };

    return stateMap[state] ?? (
      <VirtualRaceSubscription virtualRace={virtualRace!} />
    );
  };

  const updateUserVirtualRace = async (status: VirtualRaceStatesTypes) => {
    if (!userVirtualRace) return;
    await coreApi.put(`user-virtual-race/${userVirtualRace._id}`, { status })
      .then((response) => {
        if (response.data) {
          setUserVirtualRace(response.data);
        }
      })
      .catch(() => {
        toast.error('Erro ao atualizar a corrida virtual');
      });
  };

  const validateActivities = async () => {
    try {
      const response = await coreApi.get(`user-virtual-race/validate-activities/${slug}`);
      setActivities(response.data.activities);
      if (response.data.isValid) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleVirtualRaceState = async () => {
    if (!virtualRace || !userVirtualRace) return;

    const currentDate = new Date();
    const startDate = new Date(virtualRace.startDate);
    const endDate = new Date(virtualRace.endDate);

    if (userVirtualRace.status === VirtualRaceStatesTypes.SETTING_GOAL) {
      setState(VirtualRaceStatesTypes.SETTING_GOAL);
      return;
    }

    if (userVirtualRace.status === VirtualRaceStatesTypes.FINISHED) {
      const isValid = await validateActivities();
      if (isValid) {
        setState(VirtualRaceStatesTypes.FINISHED);
      }
      return;
    }

    if (isBefore(currentDate, startDate)) {
      if (userVirtualRace.status !== VirtualRaceStatesTypes.WAITING_START) {
        await updateUserVirtualRace(VirtualRaceStatesTypes.WAITING_START);
      }
      setState(VirtualRaceStatesTypes.WAITING_START);
      return;
    }

    if (isWithinInterval(currentDate, { start: startDate, end: endDate })) {
      const isValid = await validateActivities();
      if (isValid) {
        if (userVirtualRace.status !== VirtualRaceStatesTypes.FINISHED) {
          await updateUserVirtualRace(VirtualRaceStatesTypes.FINISHED);
        }
        setState(VirtualRaceStatesTypes.FINISHED);
        return;
      }

      if (userVirtualRace.status === VirtualRaceStatesTypes.WAITING_VALIDATION) {
        setState(VirtualRaceStatesTypes.WAITING_VALIDATION);
        return;
      }

      if (userVirtualRace.status !== VirtualRaceStatesTypes.IN_PROGRESS) {
        await updateUserVirtualRace(VirtualRaceStatesTypes.IN_PROGRESS);
      }
      setState(VirtualRaceStatesTypes.IN_PROGRESS);
      return;
    }

    if (isAfter(currentDate, endDate)) {
      const isValid = await validateActivities();
      if (isValid) {
        if (userVirtualRace.status !== VirtualRaceStatesTypes.FINISHED) {
          await updateUserVirtualRace(VirtualRaceStatesTypes.FINISHED);
        }
        setState(VirtualRaceStatesTypes.FINISHED);
        return;
      }

      if (userVirtualRace.status !== VirtualRaceStatesTypes.OUT_OF_DATE) {
        await updateUserVirtualRace(VirtualRaceStatesTypes.OUT_OF_DATE);
      }
      setState(VirtualRaceStatesTypes.OUT_OF_DATE);
    }
  };

  const checkIfCompanyChallenge = (virtualRaceData: VirtualRaceType) => {
    if (virtualRaceData && !userVirtualRace && (virtualRaceData.isCompanyRace
        && (company_id === virtualRaceData.companyId
        || companies?.some((company) => company.id === virtualRaceData.companyId)))) {
      return true;
    }
    return false;
  };

  const createCompanyUserVirtualRace = async (virtualRaceData: VirtualRaceType) => {
    if (checkIfCompanyChallenge(virtualRaceData)) {
      const data = {
        user_id: user.id,
        weeks: 0,
        slug: virtualRaceData.slug,
        status: VirtualRaceStatesTypes.SETTING_GOAL,
      };

      await coreApi.post('user-virtual-race', data)
        .then((response) => {
          if (response.data) {
            setUserVirtualRace(response.data);
            setState(VirtualRaceStatesTypes.SETTING_GOAL);
            toast.success('Corrida virtual iniciada com sucesso!');
          }
        })
        .catch(() => {
          toast.error('Erro ao iniciar a corrida virtual');
        });
    }
  };

  const fetchVirtualRace = async (): Promise<VirtualRaceType> => {
    try {
      const response = await api2.get(`/virtual-race/slug/${slug}`);
      setVirtualRace(response.data);
      return response.data;
    } catch (error) {
      toast.error('Erro ao carregar os detalhes da corrida virtual');
      throw new Error('Failed to fetch virtual race detail');
    }
  };

  const fetchUserVirtualRace = async (): Promise<UserVirtualRaceType> => {
    try {
      const response = await coreApi.get(`user-virtual-race/${slug}`);
      setUserVirtualRace(response.data);
      setState(response.data.status);
      return response.data;
    } catch {
      toast.error('Erro ao carregar a corrida virtual');
      throw new Error('Failed to fetch user virtual race');
    }
  };

  const handleVirtualRaceInfo = async () => {
    setLoading(true);

    const virtualRaceData = await fetchVirtualRace();

    if (!user.id && !token) {
      setState(VirtualRaceStatesTypes.SUBSCRIPTION);
      setLoading(false);
      return;
    }

    if (!virtualRaceData) {
      return;
    }

    if (virtualRaceData.isCompanyRace) {
      const userVirtualRaceData = await fetchUserVirtualRace();
      if (!userVirtualRaceData) {
        await createCompanyUserVirtualRace(virtualRaceData);
      }
    } else {
      await api2.get(`user-virtual-race/verify/${slug}`);
      const userVirtualRaceData = await fetchUserVirtualRace();
      if (!userVirtualRaceData) {
        setState(VirtualRaceStatesTypes.SUBSCRIPTION);
      }
    }
    setLoading(false);
  };
  useEffect(() => {
    handleVirtualRaceInfo();
    dispatch(getActivities(user.id) as any);
  }, []);

  useEffect(() => {
    handleVirtualRaceState();
  }, [virtualRace, userVirtualRace]);

  useEffect(() => {
    if (state === VirtualRaceStatesTypes.SUBSCRIPTION) {
      setShowIntegrationCard(false);
      return;
    }
    setShowIntegrationCard(true);
  }, [state]);

  return (
    <Container>
      <BackArrow text={virtualRace?.title || 'Corrida Virtual'} bigger backTo="/club/corrida-virtual" />
      <Content>
        {loading
          && (
            <LoadingContainer>
              <Loader color="white" />
            </LoadingContainer>
          )}
        {!loading && (
          <Card>
            {virtualRace && (
              <VirtualRaceHeader
                virtualRace={virtualRace}
                userVirtualRace={userVirtualRace}
              />
            )}
            {slug && virtualRace && renderCurrentState()}
          </Card>
        )}
      </Content>
      {showIntegrationCard && (state === VirtualRaceStatesTypes.IN_PROGRESS || state === VirtualRaceStatesTypes.WAITING_START) && <VirtualRaceIntegrationCard action={() => setShowIntegrationCard(false)} />}
    </Container>
  );
}
